import { useMemo, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Box, Skeleton, Typography, CircularProgress } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { Chart } from 'react-chartjs-2';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
  TooltipModel,
  ChartData,
  ScatterController
} from 'chart.js';
import momentTZ from 'moment-timezone';
import moment from 'moment';

import { queryClient } from 'components/providers/QueryProvider';
import custom_palettes from 'theme/custom_palettes';
import api from 'utils/api';
import { useConstants } from 'utils/hooks';
import { myChartJsTooltipHandler } from 'utils/chart';

import { cardSetAtom, endAtom, startAtom } from '../state';

const fetchMedianGraphKey = ['median-nav-chart'];

export const fetchMedianGraph = () => {
  queryClient.fetchQuery({
    queryKey: fetchMedianGraphKey
  });
};

type EventType = {
  startTs: string;
  endTs: string;
  eventType: string;
  comment: string;
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ScatterController
);

// @ts-ignore
Tooltip.positioners.custom = function (elements, eventPosition) {
  /** @type {Chart.Tooltip} */
  var tooltip = this;

  /* ... */

  return {
    x: eventPosition.x,
    y: eventPosition.y
  };
};

const externalTooltipHandler = (context: {
  chart: ChartJS<'line'>;
  tooltip: TooltipModel<'line'>;
}) => {
  myChartJsTooltipHandler(context, (tooltip: any) => {
    const data = tooltip.dataPoints[0].raw;
    const unit = tooltip.chart.scales.x._unit;
    const format = 'DD MMM YYYY';
    const label = `${momentTZ(data.x).format(format)}`;
    const value = data.y;
    const dataset = tooltip.dataPoints[0].dataset;
    const title = dataset.label.split('(');
    if (data.pointType === 'EVENT') {
      return `
              <div class="chart-event-tooltip-holder">
                <div><span class="cust-tt-label">Name:</span> <span class="cust-tt-value">${data.eventType}</span></div>
                <div><span class="cust-tt-label">Comment:</span> <span class="cust-tt-value">${data.comment}</span></div>
                <div><span class="cust-tt-label">Start:</span> <span class="cust-tt-value">${momentTZ.utc(data.x).tz('Asia/Singapore')}</span></div>
                <div><span class="cust-tt-label">End:</span> <span class="cust-tt-value">${momentTZ.utc(Number.parseInt(data.endTs)).tz('Asia/Singapore')}</span></div>
              </div>
              `;
    }
    const body = `<div class="label">${label}</div>
    <div class="values" style="color: ${dataset.backgroundColor}">
      <div class="q">${title[0]}</div>
      <div class="a">${value} ${title[1].replace(')', '')}</div>
    </div>
    `;
    return body;
  });
};

export const NavigatorChart = () => {
  const { CONSTANTS } = useConstants();

  // Time
  const start = useRecoilValue(startAtom);
  const end = useRecoilValue(endAtom);

  // Location, Param, Depth
  const paramDepths = useRecoilValue<
    Array<{
      location: string;
      parameterDesc: string;
      depth: { value: string };
      sensorDetailsHash: string;
      unit: string;
    }>
  >(cardSetAtom);

  const selectedParamDepth = useMemo(() => {
    return paramDepths.length > 0 ? paramDepths.at(0) : undefined;
  }, [paramDepths]);

  const { data, error, isPending } = useQuery<
    {
      medianValues: Array<{ timestamp: string; median: number }>;
      events: Array<EventType>;
    },
    Error & { response: { data: { message?: string } } }
  >({
    queryKey: fetchMedianGraphKey,
    queryFn: () =>
      api
        .post(`/api/rawData/median`, {
          from: start,
          to: end,
          location:
            CONSTANTS.LOCATION_FULL_FORM_TO_ACRONYM_MAP[
              selectedParamDepth?.location ?? ''
            ],
          parameterDesc: selectedParamDepth?.parameterDesc,
          depth: selectedParamDepth?.depth.value,
          sensorDetailsHash: selectedParamDepth?.sensorDetailsHash
        })
        .then((res) => res.data),
    staleTime: 0,
    enabled: false // Disable auto fetching
  });

  const chartRef = useRef<ChartJS<'line'> | null>(null);

  const medianValues = useMemo(() => {
    return (
      (data?.medianValues ?? [])
        // .filter(({ median }) => median !== null)
        .map(({ timestamp, median }) => ({
          x: Number.parseInt(timestamp),
          y: median,
          pointType: 'MEDIAN'
        }))
    );
  }, [data]);

  const events = useMemo(() => {
    const medianV = medianValues
      .map(({ x, y }) => y)
      .filter((val: number | null) => !!val);
    let max = 30;
    if (medianV.length) {
      max = Math.max(...medianV);
    }
    max += 1;
    return (data?.events ?? []).map(
      ({ startTs, endTs, eventType, comment }) => ({
        x: Number.parseInt(startTs),
        y: max,
        eventType,
        comment,
        startTs,
        endTs,
        pointType: 'EVENT'
      })
    );
  }, [data, medianValues]);

  const fullData: ChartData<'line' | 'scatter', { x: number; y: number }[]> =
    useMemo(() => {
      return {
        datasets: [
          {
            yAxisID: 'y',
            borderColor: custom_palettes.blue[800],
            backgroundColor: custom_palettes.blue[800],
            pointStyle: 'circle',
            pointBackgroundColor: custom_palettes.white.main,
            segment: {
              borderDash: (ctx: any) =>
                ctx.p0.raw.qcFlag == 0 ? [3, 3] : undefined
            },
            label: `${selectedParamDepth?.parameterDesc} (${selectedParamDepth?.unit})`,
            data: medianValues
          },
          {
            yAxisID: 'y',
            borderColor: custom_palettes.red.main,
            backgroundColor: custom_palettes.red.main,
            pointStyle: 'triangle',
            pointBackgroundColor: custom_palettes.red.main,
            label: `${selectedParamDepth?.parameterDesc} (${selectedParamDepth?.unit})`,
            data: events,
            type: 'scatter'
          }
        ]
      };
    }, [medianValues, events]);

  const chartConfig = useMemo(() => {
    const options: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          display: false,
          position: 'top',
          align: 'center',
          labels: {
            usePointStyle: false,
            font: {
              size: 15
            },
            color: custom_palettes.gray[800]
          }
        },
        tooltip: {
          enabled: false,
          position: 'nearest',
          external: externalTooltipHandler
        }
      },
      elements: {
        point: {
          radius: 3
        },
        line: {
          borderWidth: 2
        }
      },
      scales: {
        x: {
          type: 'time',
          time: {
            unit: 'day'
          },
          ticks: {
            callback: function (value: any, index: any, ticks: any) {
              return moment(value).format('D MMM YYYY');
            }
          },
          border: {
            color: custom_palettes.gray[800],
            width: 1
          }
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          grid: { display: false },
          border: {
            color: custom_palettes.blue[800],
            width: 3
          }
        }
      },
      parsing: {
        xAxisKey: 'x'
      }
    };
    return options;
  }, [medianValues, events, selectedParamDepth]);

  return error ? (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '24px',
        margin: '0 32px',
        borderRadius: '8px',
        backgroundColor: `oklch(88.5% 0.062 18.334)`
      }}
    >
      <span>
        Oh no,an error occurred fetching the median data for this parameter and
        time range.
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <span style={{ fontWeight: 'bold' }}>Error Details:</span>
        <span
          style={{
            fontFamily: 'monospace',
            padding: '8px',
            borderRadius: '8px',
            backgroundColor: 'oklch(98.5% 0 0)'
          }}
        >
          {error.response?.data?.message ?? error.message}
        </span>
      </div>
    </div>
  ) : isPending ? (
    <Box
      sx={{
        height: 400,
        margin: '0 32px',
        display: 'flex',
        borderRadius: '12px',
        overflow: 'clip',
        position: 'relative'
      }}
    >
      <Skeleton
        variant='rectangular'
        width='100%'
        height='100%'
        animation='wave'
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none' // Prevents the overlay from capturing mouse events
        }}
      >
        <div
          style={{ display: 'inline-flex', alignItems: 'center', gap: '16px' }}
        >
          <CircularProgress
            size='1.5rem'
            sx={{ display: 'flex', color: 'oklch(70.8% 0 0)' }}
          />
          <Typography
            variant='body1'
            sx={{ display: 'flex', color: 'oklch(43.9% 0 0)' }}
          >
            Loading...
          </Typography>
        </div>
      </Box>
    </Box>
  ) : !data ? (
    <div
      style={{
        padding: '0 64px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}
    >
      <span style={{ fontWeight: 'bold' }}>Median Values:</span>
      <span>
        No median values present for this location/parameter/depth for the
        selected time range. Please try again or contact an admin.
      </span>
    </div>
  ) : (
    <div
      style={{
        padding: '0 32px'
      }}
    >
      <Chart<'line' | 'scatter'>
        type='line'
        ref={chartRef}
        options={chartConfig}
        data={fullData}
      />
    </div>
  );
};
